import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API_ROOT } from "@/config";
import SEO from "@/components/seo";
import { Check, X, ArrowLeft, CheckCircle2, Loader2, HelpCircle } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import React from 'react';
import FeatureRequestButton from "@/components/ui/feature-request-button";
import { createCreatorResultsTour, createBrandResultsTour, tourStyles } from "@/components/ui/tour-utils";
import LoadingSpinner from "@/components/ui/loading-spinner";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const getScoreGradient = (score: number) => {
  if (score >= 90) return { start: PALETTE.inchworm, end: '#22c55e' }; // Green: 90-100%
  if (score >= 61) return { start: '#facc15', end: '#fbbf24' }; // Yellow: 61-89%
  return { start: '#ef4444', end: '#dc2626' }; // Red: 0-60%
};

// Circular Compliance Score Overlay Component
const ComplianceScoreOverlay = ({ 
  currentScore,
  isVideoPlaying
}: { 
  currentScore: number | null;
  isVideoPlaying: boolean;
}) => {
  const current = Math.round(currentScore ?? 0);
  
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (current / 100) * circumference;
  
  const getScoreColor = (score: number) => {
    if (score >= 90) return PALETTE.inchworm;
    if (score >= 61) return '#fbbf24';
    return '#f43f5e';
  };

  const scoreColor = getScoreColor(current);
  
  if (currentScore === null || isVideoPlaying) return null;
  
  return (
    <div
      className="compliance-score-circle absolute top-4 right-4 z-20 flex flex-col items-center gap-2 p-3 rounded-full backdrop-blur-sm"
      style={{
        background: 'rgba(255, 255, 255, 0.75)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div className="relative w-20 h-20">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={PALETTE.americanSilver}
            strokeWidth="6"
            opacity="0.3"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={scoreColor}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold tracking-tight" style={{ color: PALETTE.gunmetal }}>
            {current}%
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Public Campaign Result - For creators who upload via campaign link
 * This is a simplified version with the SAME layout as video-result but without auth complexity
 */
const PublicCampaignResult = () => {
  const { unique_id } = useParams<{ unique_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [campaignUuid, setCampaignUuid] = useState<string | null>(
    new URLSearchParams(location.search).get('campaign')
  );
  
  const [analysis, setAnalysis] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [creatorComments, setCreatorComments] = useState<Record<number, string>>({});
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [videoHeight, setVideoHeight] = useState<number>(600);
  const gridRef = useRef<any>(null);

  // Tour function - different tour for creator (pending) vs brand (submitted)
  const startTour = () => {
    if (submissionComplete) {
      // Brand user viewing submitted results
      const tour = createBrandResultsTour();
      tour.start();
    } else {
      // Creator viewing pending results (can still submit)
      const tour = createCreatorResultsTour();
      tour.start();
    }
  };

  // Fetch analysis
  useEffect(() => {
    if (!unique_id) {
      setError("Missing analysis ID");
      setIsLoading(false);
      return;
    }

    const fetchAnalysis = async () => {
      try {
        console.log("🔄 Fetching public campaign analysis:", unique_id);
        
        // Try with campaign UUID if available, otherwise let backend find it
        const url = campaignUuid 
          ? `${API_ROOT}/campaign-analysis/${unique_id}?campaign_uuid=${campaignUuid}`
          : `${API_ROOT}/campaign-analysis/${unique_id}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Failed to load: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("✅ Analysis loaded");
        
        setAnalysis(data);
        
        // Extract campaign UUID from analysis data if not already set
        if (data.campaign_uuid && !campaignUuid) {
          setCampaignUuid(data.campaign_uuid);
        }
        
        if (data.submission_status === 'submitted') {
          setSubmissionComplete(true);
          if (data.creator_comments && Array.isArray(data.creator_comments)) {
            const commentsMap: Record<number, string> = {};
            data.creator_comments.forEach((c: any) => {
              if (c.requirement_number && c.comment) {
                commentsMap[c.requirement_number] = c.comment;
              }
            });
            setCreatorComments(commentsMap);
          }
        }
        
        // Set video URL - handle cases where video is not available
        if (data.video_url) {
          console.log("🎥 Setting video URL:", data.video_url);
          setVideoUrl(data.video_url);
        } else if (data.video_error) {
          console.warn("⚠️ Video not available:", data.video_error);
          setVideoUrl(null);
          // Show error message to user
          if (!error) {
            setError(data.video_error);
          }
        } else {
          // Fallback: try public endpoint (for legacy videos that might still exist)
          const fallbackUrl = `${API_ROOT}/public-video/${unique_id}`;
          console.log("🔄 No video_url in response, trying fallback:", fallbackUrl);
          setVideoUrl(fallbackUrl);
        }
        
      } catch (err) {
        console.error("❌ Error:", err);
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [unique_id]);

  // Video height listener - matches video player 9:16 aspect ratio
  useEffect(() => {
    const updateHeight = () => {
      if (videoContainerRef.current) {
        const width = videoContainerRef.current.offsetWidth;
        // 9:16 aspect ratio
        const calculatedHeight = Math.round((width * 16) / 9);
        setVideoHeight(calculatedHeight);
      }
    };
    
    // Initial calculation
    updateHeight();
    
    // Update on resize
    window.addEventListener('resize', updateHeight);
    
    // Small delay to ensure DOM is fully rendered
    setTimeout(updateHeight, 100);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [videoUrl]);

  // Show feedback modal when analysis first loads (only for pending submissions)
  useEffect(() => {
    if (!isLoading && analysis && !submissionComplete) {
      // Small delay to ensure page is rendered before showing modal
      setTimeout(() => {
        setShowFeedbackModal(true);
      }, 500);
    }
  }, [isLoading, analysis, submissionComplete]);

  // Submit handler
  const handleSubmit = async () => {
    if (!campaignUuid || !unique_id) return;
    
    setIsSubmitting(true);
    try {
      // Collect comments from grid data
      const commentsArray: any[] = [];
      if (gridRef.current?.api) {
        gridRef.current.api.forEachNode((node: any) => {
          if (node.data?.requirementNumber && node.data?.comment && node.data.comment.trim().length > 0) {
            commentsArray.push({
              requirement_number: node.data.requirementNumber,
              comment: node.data.comment
            });
          }
        });
      }
      
      const response = await fetch(`${API_ROOT}/submit-campaign-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: unique_id,
          campaign_uuid: campaignUuid,
          creator_comments: commentsArray
        })
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      const responseData = await response.json();
      
      // Update analysis with the submitted_at timestamp from backend
      setAnalysis((prev: any) => ({
        ...prev,
        submitted_at: responseData.submitted_at || new Date().toISOString(),
        submission_status: 'submitted'
      }));
      
      setSubmissionComplete(true);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Submission error:', err);
      alert('Failed to submit video. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get requirements - matches VideoResult structure
  // Non-compliant requirements appear first, then compliant ones
  const getRequirements = () => {
    if (!analysis) return [];
    
    const requirements: any[] = [];
    let reqNum = 1;
    
    // Helper function to extract requirement text and remove "Element:" prefix if present
    const extractRequirementText = (item: any): string => {
      let text = '';
      if (typeof item === 'string') {
        text = item;
      } else {
        text = item.input || item.requirement || item.element || item.text || '';
      }
      // Remove "Element:" prefix if present (added by external API)
      if (text.startsWith('Element: ')) {
        text = text.substring(9); // Remove "Element: " (9 characters)
      } else if (text.startsWith('Element:')) {
        text = text.substring(8); // Remove "Element:" (8 characters)
      }
      return text.trim();
    };
    
    // Helper to determine if requirement is logo or product related
    const getRequirementMetadata = (text: string) => {
      const lowerText = text.toLowerCase();
      if (lowerText.includes('brand logo')) {
        return {
          type: 'logo',
          imageUrl: analysis.brand_logo_path
        };
      } else if (lowerText.includes('product') && (lowerText.includes('visible') || lowerText.includes('shown'))) {
        return {
          type: 'product',
          imageUrl: analysis.product_image_path
        };
      }
      return null;
    };
    
    // Process non-compliant requirements FIRST (at the top)
    if (analysis.non_compliant && Array.isArray(analysis.non_compliant)) {
      analysis.non_compliant.forEach((item: any) => {
        const currentReqNum = reqNum++;
        const requirementText = extractRequirementText(item);
        const metadata = getRequirementMetadata(requirementText);
        
        requirements.push({
          requirementNumber: currentReqNum,
          requirement: requirementText,
          compliance: '❌',
          ai_answer: typeof item === 'object' ? item.ai_answer : undefined,
          comment: creatorComments[currentReqNum] || '',
          metadata: metadata
        });
      });
    }
    
    // Process compliant requirements AFTER non-compliant ones
    if (analysis.compliant && Array.isArray(analysis.compliant)) {
      analysis.compliant.forEach((item: any) => {
        const currentReqNum = reqNum++;
        const requirementText = extractRequirementText(item);
        const metadata = getRequirementMetadata(requirementText);
        
        requirements.push({
          requirementNumber: currentReqNum,
          requirement: requirementText,
          compliance: '✅',
          ai_answer: typeof item === 'object' ? item.ai_answer : undefined,
          comment: creatorComments[currentReqNum] || '',
          metadata: metadata
        });
      });
    }
    
    return requirements;
  };

  // Get score
  const getScore = () => {
    if (analysis?.compliance_score !== undefined) {
      return analysis.compliance_score;
    }
    const reqs = getRequirements();
    if (reqs.length === 0) return 0;
    const compliant = reqs.filter(r => r.compliance === '✅').length;
    return Math.round((compliant / reqs.length) * 100);
  };

  // AG Grid columns - matches VideoResult exactly
  const columnDefs: ColDef[] = [
    {
      headerName: 'Alignment',
      field: 'compliance',
      width: 120,
      resizable: false,
      filter: false,
      cellStyle: { 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
      },
      cellRenderer: (params: ICellRendererParams) => {
        const isCompliant = params.value === '✅';
        return (
          <div 
            className="compliance-icon-container flex items-center justify-center h-full w-full cursor-help"
            style={{ margin: '0 auto' }}
          >
            <div
              className="compliance-icon flex items-center justify-center rounded-full"
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: isCompliant ? PALETTE.inchworm : '#f43f5e'
              }}
            >
              {isCompliant ? (
                <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
              ) : (
                <X className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
              )}
            </div>
          </div>
        );
      },
      tooltipValueGetter: (params: any) => {
        const aiAnswer = params.data?.ai_answer;
        if (!aiAnswer) return null;
        
        const isCompliant = params.data?.compliance === '✅';
        const status = isCompliant ? 'Compliant' : 'Non-Compliant';
        return `${status}: ${aiAnswer}`;
      }
    },
    {
      headerName: 'Requirement',
      field: 'requirement',
      flex: 1,
      resizable: false,
      filter: false,
      wrapText: false,
      autoHeight: false,
      cellStyle: { 
        lineHeight: '1.4', 
        paddingTop: '8px', 
        paddingBottom: '8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      cellRenderer: (params: ICellRendererParams) => {
        const requirement = params.value || '';
        const metadata = params.data?.metadata;
        
        // If this is a logo or product requirement with an image, show it
        if (metadata && metadata.imageUrl) {
          return (
            <div className="flex items-center gap-2 w-full">
              <img 
                src={metadata.imageUrl}
                alt={metadata.type === 'logo' ? 'Brand Logo' : 'Product'} 
                className="h-6 w-auto object-contain flex-shrink-0"
                style={{ maxWidth: '40px' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div 
                className="flex-1 truncate"
                style={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: PALETTE.gunmetal
                }}
              >
                {requirement}
              </div>
            </div>
          );
        }
        
        return (
          <div 
            className="w-full truncate"
            style={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: PALETTE.gunmetal
            }}
          >
            {requirement}
          </div>
        );
      },
      tooltipValueGetter: (params: any) => {
        return params.value || '';
      },
      tooltipField: 'requirement'
    },
    {
      headerName: 'Comment',
      field: 'comment',
      width: 180,
      resizable: false,
      filter: false,
      editable: false, // Disable default AG Grid editing
      headerClass: 'comment-column-header',
      cellStyle: { 
        display: 'flex',
        alignItems: 'center',
        padding: '4px'
      },
      cellRenderer: (params: ICellRendererParams) => {
        const comment = params.value || '';
        const reqNum = params.data?.requirementNumber;
        
        // If submitted, show plain text
        if (submissionComplete) {
          if (!comment) {
            return <span className="comment-column-cell"></span>;
          }
          return (
            <span 
              className="comment-column-cell"
              style={{ 
                color: PALETTE.gunmetal,
                fontSize: '13px'
              }}
            >
              {comment}
            </span>
          );
        }
        
        // If not submitted, show editable input
        return (
          <input
            type="text"
            defaultValue={comment}
            placeholder="Click to add comment..."
            maxLength={500}
            className="comment-input-placeholder comment-column-input"
            onChange={(e) => {
              const newValue = e.target.value;
              // Update the row data directly without triggering parent re-render
              if (params.node) {
                params.node.setDataValue('comment', newValue);
              }
            }}
            onBlur={(e) => {
              // Sync to parent state when done editing
              const newValue = e.target.value;
              if (reqNum) {
                setCreatorComments(prev => ({
                  ...prev,
                  [reqNum]: newValue
                }));
              }
              e.target.style.border = '1px solid transparent';
              e.target.style.background = 'transparent';
            }}
            style={{
              width: '100%',
              padding: '4px 6px',
              border: '1px solid transparent',
              borderRadius: '4px',
              fontSize: '13px',
              color: PALETTE.gunmetal,
              background: 'transparent',
              outline: 'none',
              transition: 'border-color 0.2s, background 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.border = `1px solid ${PALETTE.americanSilver}`;
              e.target.style.background = PALETTE.brightSnow;
            }}
          />
        );
      }
    }
  ];

  // Loading
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading analysis..." fullPage />;
  }

  // Error
  if (error || !analysis) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PALETTE.brightSnow }}>
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-xl font-semibold" style={{ color: PALETTE.gunmetal }}>
            {error || "Analysis not found"}
          </p>
        </div>
      </div>
    );
  }

  const requirements = getRequirements();
  const score = getScore();
  const gradient = getScoreGradient(score);

  // Get feedback message based on compliance score
  const getFeedbackMessage = () => {
    if (score === 100) {
      return {
        title: "🎉 Perfect Match!",
        message: "Your video fully matches the brief. Awesome work — this one's good to go!",
        accentColor: PALETTE.inchworm // #B1FA63
      };
    } else if (score >= 80) {
      return {
        title: "🚀 Almost There!",
        message: "You've nailed the core requirements. Take a quick look at the not aligned points to reach 100%.",
        accentColor: '#fbbf24'
      };
    } else {
      return {
        title: "✍️ Quick Fix Needed",
        message: "A few key elements are missing. Check again the requirements in the campaign brief — you've got this!",
        accentColor: '#f43f5e'
      };
    }
  };

  const feedbackMessage = getFeedbackMessage();

  // Show buttons ONLY if accessed via campaign query param (creator flow)
  // Brand users viewing from sidebar won't have the campaign query param
  const hasCampaignParam = new URLSearchParams(location.search).get('campaign') !== null;
  const hasButtons = !submissionComplete && hasCampaignParam;
  
  console.log("🔘 Button visibility check:", {
    hasCampaignParam,
    submissionComplete,
    hasButtons,
    currentURL: location.pathname + location.search
  });
  
  return (
    <div className="flex h-screen overflow-hidden">
      <SEO
        title="Campaign Video Results"
        description="View your video compliance results for this campaign. See how your content matches the brand brief requirements."
        path={`/campaign-result/${unique_id}`}
      />
      {/* Main Content */}
      <div 
        className="flex-1 flex flex-col px-8 py-4 pb-24 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden relative"
      >
        {/* Feature Request & Help/Tour Buttons - Top Right */}
        <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
          <FeatureRequestButton />
          <button
            onClick={startTour}
            aria-label="Help / Tour"
            className="tour-help-btn bg-white border border-gray-300 shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition"
            style={{ boxShadow: '0 2px 8px rgba(36,56,55,0.10)' }}
          >
            <HelpCircle size={20} color={PALETTE.gunmetal} />
          </button>
        </div>

        {/* Powered By - Bottom Right */}
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-4">
          <span className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
            powered by{' '}
            <a 
              href="https://brandcomply.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: PALETTE.gunmetal }}
            >
              BrandComply
            </a>
          </span>
        </div>

        {/* Feedback Modal */}
        <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
          <DialogContent 
            className="sm:max-w-md border-0"
            style={{
              backgroundColor: PALETTE.brightSnow,
              borderRadius: '12px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)'
            }}
          >
            <div className="space-y-6">
              <div className="text-center">
                {/* SVG Circle Progress - matching video overlay style */}
                <div className="inline-block mb-4">
                  <div className="relative w-28 h-28">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="42"
                        fill="none"
                        stroke={PALETTE.americanSilver}
                        strokeWidth="7"
                        opacity="0.2"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="42"
                        fill="none"
                        stroke={feedbackMessage.accentColor}
                        strokeWidth="7"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 42}
                        strokeDashoffset={2 * Math.PI * 42 - (score / 100) * (2 * Math.PI * 42)}
                        style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span 
                        className="text-3xl font-bold tracking-tight"
                        style={{ color: PALETTE.gunmetal }}
                      >
                        {Math.round(score)}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <DialogTitle 
                  className="text-xl font-semibold mb-2"
                  style={{ color: PALETTE.gunmetal }}
                >
                  {feedbackMessage.title}
                </DialogTitle>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: `${PALETTE.gunmetal}99` }}
                >
                  {feedbackMessage.message}
                </p>
              </div>
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="w-full py-3 text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                style={{
                  backgroundColor: PALETTE.gunmetal,
                  color: PALETTE.brightSnow,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Got it!
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Modal - Shown after successful submission */}
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent 
            className="sm:max-w-md border-0"
            style={{
              backgroundColor: PALETTE.brightSnow,
              borderRadius: '12px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)'
            }}
          >
            <div className="space-y-6">
              <div className="text-center">
                {/* Inchworm Green Checkmark Icon */}
                <div className="inline-block mb-4">
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
                    style={{ backgroundColor: PALETTE.inchworm }}
                  >
                    <Check className="w-10 h-10" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                  </div>
                </div>
                
                <DialogTitle 
                  className="text-xl font-semibold mb-2"
                  style={{ color: PALETTE.gunmetal }}
                >
                  Video Submitted Successfully!
                </DialogTitle>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: `${PALETTE.gunmetal}99` }}
                >
                  Your video has been submitted to the agency/brand for review. You'll receive a notification once they've reviewed your submission.
                </p>
              </div>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full py-3 text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                style={{
                  backgroundColor: PALETTE.gunmetal,
                  color: PALETTE.brightSnow,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Tour styles */}
        <style>{tourStyles}</style>

        {/* Process Wizard and Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            {hasButtons ? (
              <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="flex items-center justify-center gap-4 mb-8 w-full max-w-4xl">
                  {['Contact Information', 'Video Upload', 'Success Scan'].map((label, idx) => {
                    const isActive = idx === 2; // Step 3 (Compliance Result) is active
                    const isCompleted = idx < 2; // Steps 1 and 2 are completed
                    return (
                      <React.Fragment key={label}>
                        <div className="flex items-center flex-1 justify-center">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                            style={{
                              background: isActive ? PALETTE.gunmetal : (isCompleted ? PALETTE.inchworm : PALETTE.americanSilver),
                              color: isActive ? PALETTE.brightSnow : PALETTE.gunmetal
                            }}
                          >
                            {idx + 1}
                          </div>
                          <span 
                            className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                            style={{ color: isActive || isCompleted ? PALETTE.gunmetal : PALETTE.americanSilver }}
                          >
                            {label}
                          </span>
                        </div>
                        {idx < 2 && (
                          <div
                            className="h-0.5 mx-2 sm:mx-4 flex-1"
                            style={{
                              minWidth: 20,
                              background: isCompleted ? PALETTE.inchworm : PALETTE.americanSilver,
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Content Area with Scroll */}
            <div className={`flex-grow flex flex-col justify-start overflow-y-auto ${hasButtons ? 'px-6' : 'px-6 pt-6'}`}>
              <div className="w-full max-w-7xl mx-auto mb-24">
            {/* Two Column Layout: Video Player (Left 25%) and Requirements Table (Right 75%) */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
              {/* Left Column: Video Player - 25% width with 9:16 aspect ratio */}
              <div className="w-full lg:col-span-1">
                {videoUrl ? (
                  <Card className="border-0 shadow-lg w-full">
                    <CardContent className="p-0">
                      <div 
                        ref={videoContainerRef}
                        className="video-player-container relative w-full rounded-lg overflow-hidden" 
                        style={{ 
                          aspectRatio: '9/16',
                          maxWidth: '100%'
                        }}
                      >
                        {/* Compliance Score Overlay - Hide when playing */}
                        <div className="compliance-score-overlay">
                          <ComplianceScoreOverlay currentScore={score} isVideoPlaying={isVideoPlaying} />
                        </div>
                        
                        {/* Submission Information Overlays - Below compliance score - Hide when playing */}
                        {!isVideoPlaying && (
                          <div 
                            className="absolute top-32 left-4 right-4 z-20 space-y-2"
                          >
                            {analysis.campaign_name && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm flex items-center">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80`, minWidth: '90px' }}>Campaign:</span>
                                  <span style={{ color: PALETTE.gunmetal }}>{analysis.campaign_name}</span>
                                </div>
                              </div>
                            )}
                            
                            {analysis.version && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm flex items-center">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80`, minWidth: '90px' }}>Version:</span>
                                  <span style={{ color: PALETTE.gunmetal }}>V{analysis.version}</span>
                                </div>
                              </div>
                            )}
                            
                            {analysis.creator_name && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm flex items-center">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80`, minWidth: '90px' }}>Creator:</span>
                                  <span style={{ color: PALETTE.gunmetal }}>{analysis.creator_name}</span>
                                </div>
                              </div>
                            )}
                            
                            {/* Show timestamp: submitted_at if submitted, created_at if pending */}
                            {(analysis?.submitted_at || analysis?.created_at) && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm flex items-center">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80`, minWidth: '90px' }}>
                                    {submissionComplete ? 'Submitted on:' : 'Reviewed on:'}
                                  </span>
                                  <span style={{ color: PALETTE.gunmetal }}>
                                    {(() => {
                                      try {
                                        // Use submitted_at if submitted, otherwise use created_at for pending
                                        const timestamp = (submissionComplete && analysis.submitted_at) 
                                          ? analysis.submitted_at 
                                          : analysis.created_at;
                                        
                                        let date: Date;
                                        if (typeof timestamp === 'number') {
                                          // Unix timestamp (seconds)
                                          date = new Date(timestamp * 1000);
                                        } else if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
                                          // SQLite UTC timestamp without timezone indicator
                                          // Format: "YYYY-MM-DD HH:MM:SS" - treat as UTC
                                          const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
                                          date = new Date(utcTimestamp);
                                        } else {
                                          // Already has timezone info or other format
                                          date = new Date(timestamp);
                                        }
                                        
                                        if (isNaN(date.getTime())) return '';
                                        
                                        return date.toLocaleString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        });
                                      } catch (e) {
                                        return '';
                                      }
                                    })()}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Video Element */}
                        <video
                          key={videoUrl}
                          ref={videoRef}
                          src={videoUrl}
                          className="w-full h-full"
                          style={{ objectFit: 'cover' }}
                          playsInline
                          preload="metadata"
                          crossOrigin="anonymous"
                          lang="en"
                          onError={(e) => {
                            console.error("Video error:", e);
                            const video = e.currentTarget;
                            console.error("Video error details:", {
                              error: video.error,
                              networkState: video.networkState,
                              readyState: video.readyState,
                              src: video.src,
                              errorCode: video.error?.code,
                              errorMessage: video.error?.message
                            });
                          }}
                          onLoadStart={() => console.log("🎥 Video load started:", videoUrl)}
                          onLoadedMetadata={() => console.log("✅ Video metadata loaded")}
                          onCanPlay={() => console.log("✅ Video can play")}
                          onPlay={() => setIsVideoPlaying(true)}
                          onPause={() => setIsVideoPlaying(false)}
                          onEnded={() => setIsVideoPlaying(false)}
                          controls
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-0 shadow-lg w-full">
                    <CardContent className="p-0">
                      <div 
                        ref={videoContainerRef}
                        className="relative w-full rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center" 
                        style={{ 
                          aspectRatio: '9/16',
                          maxWidth: '100%',
                          minHeight: '400px'
                        }}
                      >
                        <div className="text-center p-8">
                          <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-gray-600 font-medium mb-2">Video Not Available</p>
                          <p className="text-sm text-gray-500">
                            {error || "This video was created before the storage migration and is no longer available."}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column: Requirements Table - 75% width, matching video height */}
              <div className="w-full lg:col-span-3 flex flex-col">
                {/* AG Grid Table - Height matches video player exactly */}
                {requirements && requirements.length > 0 ? (
                  <>
                    <style>{`
                      .ag-theme-quartz .ag-row {
                        border-bottom: none !important;
                      }
                      .ag-theme-quartz .ag-row-even {
                        border-bottom: none !important;
                      }
                      .ag-theme-quartz .ag-row-odd {
                        border-bottom: none !important;
                      }
                      .ag-theme-quartz .ag-row:hover {
                        background-color: rgba(209, 209, 209, 0.2) !important;
                      }
                      .comment-input-placeholder::placeholder {
                        font-style: italic;
                        color: ${PALETTE.gunmetal}60;
                        opacity: 1;
                      }
                    `}</style>
                    <div 
                      className="ag-theme-quartz requirements-table" 
                      style={{ 
                        width: '100%',
                        height: videoHeight ? `${videoHeight}px` : '600px'
                      }}
                    >
                      <AgGridReact
                        ref={gridRef}
                        rowData={requirements}
                        columnDefs={columnDefs}
                        pagination={false}
                        rowHeight={40}
                        suppressPaginationPanel={true}
                        domLayout="normal"
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8" style={{ color: PALETTE.gunmetal }}>
                    No requirements data available
                  </div>
                )}
                
                {/* Disclaimer */}
                <p className="text-xs text-center mt-2" style={{ color: `${PALETTE.gunmetal}50` }}>
                  BrandComply can make mistakes. Please verify important information.
                </p>
              </div>
            </div>
          </div>
            </div>
          </main>
        </div>
      </div>

      {/* Bottom Buttons - ALWAYS VISIBLE for creators */}
      {hasButtons && (
        <div 
          className="fixed bottom-8 left-0 right-0 z-20"
        >
          <div className="flex items-center justify-center gap-4">
            {/* Re-Upload Button */}
            <Button
              onClick={() => navigate(`/upload/${campaignUuid}`)}
              variant="outline"
              className="reupload-btn rounded-full px-6 h-12 flex items-center justify-center gap-2"
              style={{ 
                background: PALETTE.brightSnow,
                color: PALETTE.gunmetal,
                border: `1px solid ${PALETTE.americanSilver}`,
                borderRadius: '9999px'
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-base font-semibold">Re-Upload Video</span>
            </Button>
            
            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="submit-btn rounded-full px-6 h-12 flex items-center justify-center gap-2"
              style={{ 
                background: isSubmitting ? PALETTE.americanSilver : PALETTE.inchworm,
                color: PALETTE.gunmetal,
                border: 'none',
                borderRadius: '9999px'
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-base font-bold">Submitting...</span>
                </>
              ) : (
                <>                  
                  <span className="text-base font-bold">Submit Video & Success Scan</span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicCampaignResult;

